/**
 * Tenant logo loading shared across every vector PDF, so the technical report
 * and the prescription report embed the same tenant-branded logo (the canvas
 * exports already draw it directly).
 *
 * Returns a JPEG (flattened over `backgroundColor`, i.e. the header navy) that
 * can be embedded as a PDF image XObject via `getDataUrlImageHex`.
 */

const loadImage = (source) =>
  new Promise((resolve) => {
    if (!source) {
      resolve(null)
      return
    }

    const image = new Image()
    const timeoutId = window.setTimeout(() => resolve(null), 5000)

    image.onload = () => {
      window.clearTimeout(timeoutId)
      resolve(image)
    }

    image.onerror = () => {
      window.clearTimeout(timeoutId)
      resolve(null)
    }

    image.src = source
  })

export const getTenantLogoSources = () => {
  const buildVersion = process.env.REACT_APP_BUILD_VERSION
  const sources = []

  if (buildVersion) {
    sources.push(
      `/tenant/${buildVersion}/media/logos/logo-white.png`,
      `/tenant/${buildVersion}/media/logos/logo-header.png`,
      `/tenant/${buildVersion}/media/logos/logo.png`,
    )
  }

  const splashLogo = document.querySelector('#splash-screen img')

  if (splashLogo?.src && !sources.includes(splashLogo.src)) {
    sources.push(splashLogo.src)
  }

  return sources
}

const getColorLuminance = (color) => {
  const value = String(color || '').trim()

  if (value.startsWith('rgb')) {
    const parts = value.match(/\d+(\.\d+)?/g)?.slice(0, 3).map(Number)

    if (parts?.length === 3) {
      return (0.2126 * parts[0] + 0.7152 * parts[1] + 0.0722 * parts[2]) / 255
    }
  }

  const normalized = value.replace('#', '')
  const hex = normalized.length === 3
    ? normalized.split('').map((part) => `${part}${part}`).join('')
    : normalized.padStart(6, '0').slice(0, 6)
  const red = parseInt(hex.slice(0, 2), 16)
  const green = parseInt(hex.slice(2, 4), 16)
  const blue = parseInt(hex.slice(4, 6), 16)

  if (![red, green, blue].every(Number.isFinite)) {
    return 1
  }

  return (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255
}

const getImageLuminance = (image) => {
  const width = image.naturalWidth || image.width
  const height = image.naturalHeight || image.height

  if (!width || !height) {
    return null
  }

  try {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')
    context.clearRect(0, 0, width, height)
    context.drawImage(image, 0, 0, width, height)

    const data = context.getImageData(0, 0, width, height).data
    let total = 0
    let count = 0

    for (let index = 0; index < data.length; index += 4) {
      const alpha = data[index + 3] / 255

      if (alpha < 0.08) {
        continue
      }

      total += ((0.2126 * data[index] + 0.7152 * data[index + 1] + 0.0722 * data[index + 2]) / 255) * alpha
      count += alpha
    }

    return count > 0 ? total / count : null
  } catch (error) {
    return null
  }
}

const getContrastScore = (logoLuminance, backgroundLuminance) => {
  const bright = Math.max(logoLuminance, backgroundLuminance)
  const dark = Math.min(logoLuminance, backgroundLuminance)

  return (bright + 0.05) / (dark + 0.05)
}

export const loadContrastingTenantLogoElement = async (backgroundColor = '#ffffff') => {
  const sources = getTenantLogoSources()
  const backgroundLuminance = getColorLuminance(backgroundColor)
  let fallback = null
  let best = null
  let bestScore = -1

  for (const source of sources) {
    const image = await loadImage(source)

    if (!image) {
      continue
    }

    if (!fallback) {
      fallback = image
    }

    const luminance = getImageLuminance(image)
    const score = luminance == null
      ? 0
      : getContrastScore(luminance, backgroundLuminance)

    if (score > bestScore) {
      best = image
      bestScore = score
    }
  }

  return best || fallback
}

export const loadTenantLogoImage = async (backgroundColor = '#ffffff') => {
  const logo = await loadContrastingTenantLogoElement(backgroundColor)

  if (!logo) {
    return null
  }

  const canvas = document.createElement('canvas')
  const width = logo.naturalWidth || logo.width
  const height = logo.naturalHeight || logo.height

  if (!width || !height) {
    return null
  }

  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  context.fillStyle = backgroundColor
  context.fillRect(0, 0, width, height)
  context.drawImage(logo, 0, 0, width, height)

  return {
    dataUrl: canvas.toDataURL('image/jpeg', 0.94),
    height,
    width,
  }
}

export const getDataUrlImageHex = (dataUrl) => {
  if (!dataUrl || !String(dataUrl).startsWith('data:image/jpeg;base64,')) {
    return ''
  }

  const binary = atob(String(dataUrl).split(',')[1] || '')
  let hex = ''

  for (let index = 0; index < binary.length; index += 1) {
    hex += binary.charCodeAt(index).toString(16).padStart(2, '0').toUpperCase()
  }

  return hex
}
